import { Moon, Sun, Menu, X, ChevronDown, LayoutGrid } from "@/lib/icons";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { NAV_CATEGORIES } from "@/data/navigation";
import BrandMark from "./BrandMark";
import LogoEasterEgg from "./LogoEasterEgg";

// Route prefixes that must never appear in public navigation.
// Mirrors Disallow rules in public/robots.txt.
const DISALLOWED_NAV_PREFIXES = ["/admin", "/dashboard", "/api", "/auth", "/internal", "/_"];

const isPublicNavPath = (path: string) =>
  !DISALLOWED_NAV_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );

/**
 * Per-category chroma accent — borrows the element-category palette so each
 * nav group carries a quiet chemistry tint without breaking the single-blue
 * primary system. Used on dropdown header strip and trigger underline only.
 */
const CATEGORY_ACCENT: Record<string, string> = {
  explore:   "var(--cat-nonmetal)",
  calculate: "var(--cat-metalloid)",
  practice:  "var(--cat-alkaline)",
  learn:     "var(--cat-transition)",
  play:      "var(--cat-alkali)",
  lab:       "var(--cat-halogen)",
  teach:     "var(--cat-noble)",
};
const accentVar = (key: string) => CATEGORY_ACCENT[key] ?? "var(--primary)";

// Filter out any disallowed paths defensively (taxonomy is curated, but stay safe).
const visibleCategories = NAV_CATEGORIES.map((cat) => ({
  ...cat,
  tools: cat.tools.filter((t) => isPublicNavPath(t.path)),
})).filter((cat) => cat.tools.length > 0);

const THEME_KEY = "theme-mode";

function getInitialTheme(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === "dark") return true;
    if (stored === "light") return false;
  } catch {
    /* ignore */
  }
  return false;
}

export default function AppHeader() {
  const [dark, setDark] = useState<boolean>(() => getInitialTheme());
  const [menuOpen, setMenuOpen] = useState(false);
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    try {
      localStorage.setItem(THEME_KEY, dark ? "dark" : "light");
    } catch {
      /* ignore */
    }
  }, [dark]);

  useEffect(() => {
    setMenuOpen(false);
    setOpenCategory(null);
  }, [location.pathname]);

  // Close dropdowns on outside click or Escape.
  useEffect(() => {
    if (!openCategory) return;
    const onClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenCategory(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenCategory(null);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [openCategory]);

  const isToolActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);
  const isCategoryActive = (catKey: string) =>
    visibleCategories
      .find((c) => c.key === catKey)
      ?.tools.some((t) => isToolActive(t.path)) ?? false;

  return (
    <header
      className="
        sticky top-0 z-40 w-full
        border-b border-border/60
        bg-background/40 backdrop-blur-xl
        supports-[backdrop-filter]:bg-background/30
        shadow-[inset_0_1px_0_hsl(var(--primary)/0.08),0_1px_0_hsl(var(--background)/0.6)]
      "
      role="banner"
    >
      <div className="max-w-[1200px] mx-auto px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-6" ref={navRef}>
          <LogoEasterEgg />

          {/* Desktop nav - grouped dropdowns */}
          <nav className="hidden md:flex items-center gap-0.5" aria-label="Main navigation">
            {visibleCategories.map((cat) => {
              const Icon = cat.icon;
              const open = openCategory === cat.key;
              const active = isCategoryActive(cat.key);
              const accent = accentVar(cat.key);
              return (
                <div key={cat.key} className="relative">
                  <button
                    type="button"
                    onClick={() => setOpenCategory(open ? null : cat.key)}
                    onMouseEnter={() => openCategory && setOpenCategory(cat.key)}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    style={{ ["--cat-accent" as string]: accent }}
                    className={`group/cat relative inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md transition-colors ${
                      active || open
                        ? "text-foreground bg-accent font-medium"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                    }`}
                  >
                    {cat.label}
                    <ChevronDown
                      size={12}
                      aria-hidden="true"
                      className={`transition-transform ${open ? "rotate-180" : ""}`}
                    />
                    {/* Chemistry-chroma underline — fades in on hover/open. */}
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none absolute left-2 right-2 bottom-0.5 h-[2px] rounded-full transition-opacity duration-150 ${
                        active || open ? "opacity-90" : "opacity-0 group-hover/cat:opacity-60"
                      }`}
                      style={{ backgroundColor: `hsl(${accent})` }}
                    />
                  </button>
                  {open && (
                    <div
                      role="menu"
                      aria-label={`${cat.label} menu`}
                      className="absolute left-0 top-full mt-1 w-72 rounded-lg border border-border bg-popover shadow-lg overflow-hidden z-50"
                    >
                      {/* 2px chroma strip identifies the category at a glance. */}
                      <div
                        aria-hidden="true"
                        className="h-[2px] w-full"
                        style={{ backgroundColor: `hsl(${accent})` }}
                      />
                      <div className="px-3 py-2 border-b border-border bg-muted/40 flex items-center gap-2">
                        <Icon
                          size={13}
                          aria-hidden="true"
                          style={{ color: `hsl(${accent})` }}
                        />
                        <span className="text-[11px] font-medium text-foreground">{cat.label}</span>
                        <span className="text-[11px] text-muted-foreground truncate">{cat.tagline}</span>
                      </div>
                      <ul className="p-1.5">
                        {cat.tools.map((tool) => {
                          const ToolIcon = tool.icon;
                          const toolActive = isToolActive(tool.path);
                          return (
                            <li key={tool.path}>
                              <Link
                                to={tool.path}
                                role="menuitem"
                                className={`flex items-start gap-2.5 px-2.5 py-2 rounded-md transition-colors ${
                                  toolActive
                                    ? "bg-accent text-foreground"
                                    : "text-foreground hover:bg-accent/60"
                                }`}
                                aria-current={toolActive ? "page" : undefined}
                              >
                                <ToolIcon size={14} className="mt-0.5 text-muted-foreground" aria-hidden="true" />
                                <span className="flex-1 min-w-0">
                                  <span className="block text-sm font-medium leading-tight">{tool.label}</span>
                                  <span className="block text-[11px] text-muted-foreground leading-snug mt-0.5">
                                    {tool.description}
                                  </span>
                                </span>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}

            <Link
              to="/explore"
              className={`inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md transition-colors ml-1 ${
                location.pathname === "/explore"
                  ? "text-foreground bg-accent font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
              }`}
              aria-current={location.pathname === "/explore" ? "page" : undefined}
            >
              <LayoutGrid size={13} aria-hidden="true" />
              All tools
            </Link>
          </nav>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setDark(!dark)}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {dark ? <Sun size={18} aria-hidden="true" /> : <Moon size={18} aria-hidden="true" />}
          </button>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
          >
            {menuOpen ? <X size={18} aria-hidden="true" /> : <Menu size={18} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile nav - grouped sections */}
      {menuOpen && (
        <nav
          id="mobile-nav"
          className="md:hidden border-t border-border bg-background px-3 py-2 max-h-[calc(100vh-3.5rem)] overflow-y-auto"
          aria-label="Mobile navigation"
        >
          {visibleCategories.map((cat) => {
            const Icon = cat.icon;
            return (
              <div key={cat.key} className="py-1.5">
                <div className="flex items-center gap-1.5 px-2 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Icon size={11} aria-hidden="true" />
                  {cat.label}
                </div>
                <ul>
                  {cat.tools.map((tool) => {
                    const toolActive = isToolActive(tool.path);
                    return (
                      <li key={tool.path}>
                        <Link
                          to={tool.path}
                          className={`block text-sm px-3 py-2 rounded-md transition-colors ${
                            toolActive
                              ? "text-foreground bg-accent font-medium"
                              : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                          }`}
                          aria-current={toolActive ? "page" : undefined}
                        >
                          {tool.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
          <div className="border-t border-border mt-2 pt-2">
            <Link
              to="/explore"
              className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md transition-colors ${
                location.pathname === "/explore"
                  ? "text-foreground bg-accent font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
              }`}
            >
              <LayoutGrid size={14} aria-hidden="true" />
              All tools
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
